"use client"

import { createContext, useContext } from "react"
import { useLanguage } from "./LanguageContext"

const PrivacyPolicyContext = createContext()

export const usePrivacyPolicy = () => {
  const context = useContext(PrivacyPolicyContext)
  if (!context) {
    throw new Error("usePrivacyPolicy must be used within a PrivacyPolicyProvider")
  }
  return context
}

const privacyPolicies = {
  en: {
    title: "Privacy Policy – Gold House Luxury Rentals",
    updatedAt: "Last updated: November 2025",
    sections: [
      {
        id: "intro",
        heading: "1. General",
        content: [
          "Gold House Luxury Rentals (\"we\", \"the site\") respects the privacy of its users.",
          "This policy explains what information we collect, how we use it, with whom it may be shared and how it is protected.",
          "Use of the site constitutes your consent to this Privacy Policy. If you do not agree with it, please do not use the site."
        ]
      },
      {
        id: "data-we-collect",
        heading: "2. Information We Collect",
        content: [
          "When you contact us via the contact form, you provide us with: name, email address, phone number (optional) and the content of your message.",
          "This information is sent to an external service (Make/Integromat) that helps us manage and respond to inquiries.",
          "You may also contact us directly by email, phone or WhatsApp. In these cases, the information is handled according to the privacy policies of the relevant providers (email, phone company, WhatsApp).",
          "According to the current code of the site, we do not use cookies for marketing tracking and we do not run Google Analytics or Meta Pixel on the client side.",
          "The site uses an external booking system API to display apartment availability. From the browser we send only apartment ID and date range, without personal details of guests."
        ]
      },
      {
        id: "use-of-data",
        heading: "3. Purposes of Use",
        content: [
          "Handling and responding to your inquiries and booking requests.",
          "Maintaining contact with you regarding offers, questions and coordination of stays.",
          "Improving our services and the content of the site (subject to any future update of this policy)."
        ]
      },
      {
        id: "sharing",
        heading: "4. Sharing Information with Third Parties",
        content: [
          "We may share information with technology providers who assist us in operating the site and managing inquiries (such as Make/Integromat, hosting providers and the external booking system).",
          "We may share information with apartment owners and other parties involved in providing the accommodation service.",
          "We will disclose information if we are required to do so by law, court order or a competent authority. We do not sell or rent your personal information to unrelated third parties for independent marketing purposes."
        ]
      },
      {
        id: "security",
        heading: "5. Data Storage and Security",
        content: [
          "Information sent through the contact form is stored in third‑party systems we use to manage inquiries.",
          "We take reasonable measures to protect information from unauthorized access, misuse or disclosure, but no system can be completely secure.",
          "You should not consider this document a guarantee of absolute security."
        ]
      },
      {
        id: "rights",
        heading: "6. Your Rights",
        content: [
          "Subject to applicable law (such as the Israeli Privacy Protection Law, as applicable), you may contact us to review, correct or delete personal information about you, to the extent that the law allows and requires.",
          "For any request regarding your personal data, you can contact us at: info@goldhouse-apart.com."
        ]
      },
      {
        id: "retention",
        heading: "7. Data Retention",
        content: [
          "We keep personal information only as long as it is needed for the purposes for which it was provided and as required by law (for example, for customer relations management or legal/financial obligations)."
        ]
      },
      {
        id: "transfer",
        heading: "8. International Data Transfers",
        content: [
          "Use of third‑party systems may involve transferring information outside of Israel or the EU (as applicable). Such transfers will be carried out in accordance with applicable law and the privacy policies of those providers."
        ]
      },
      {
        id: "changes",
        heading: "9. Changes to this Policy",
        content: [
          "We may update this Privacy Policy from time to time. The updated version will be published on the site and will take effect upon publication.",
          "Continued use of the site after such changes constitutes your acceptance of the updated policy."
        ]
      },
      {
        id: "contact",
        heading: "10. Contact Us",
        content: [
          "For any question regarding this Privacy Policy, you can contact us at:",
          "Email: info@goldhouse-apart.com",
          "Phone: +972-58-420-4297"
        ]
      }
    ]
  },
  fr: {
    title: "Politique de Confidentialité – Gold House Luxury Rentals",
    updatedAt: "Dernière mise à jour : novembre 2025",
    sections: [
      {
        id: "intro",
        heading: "1. Généralités",
        content: [
          "Gold House Luxury Rentals (\"nous\", \"le site\") respecte la vie privée de ses utilisateurs.",
          "Cette politique explique quelles informations nous collectons, comment nous les utilisons, avec qui elles peuvent être partagées et comment elles sont protégées.",
          "L'utilisation du site vaut acceptation de la présente Politique de Confidentialité. Si vous n'êtes pas d'accord, veuillez ne pas utiliser le site."
        ]
      },
      {
        id: "data-we-collect",
        heading: "2. Informations que nous collectons",
        content: [
          "Lorsque vous nous contactez via le formulaire de contact, vous nous fournissez : nom, adresse e‑mail, numéro de téléphone (optionnel) et contenu de votre message.",
          "Ces informations sont envoyées à un service externe (Make/Integromat) qui nous aide à gérer et traiter les demandes.",
          "Vous pouvez également nous contacter directement par e‑mail, téléphone ou WhatsApp. Dans ces cas, les informations sont traitées conformément aux politiques de confidentialité des fournisseurs concernés (messagerie, opérateur téléphonique, WhatsApp).",
          "Selon le code actuel du site, nous n'utilisons pas de cookies pour le suivi marketing et nous n'exécutons pas Google Analytics ni Meta Pixel côté client.",
          "Le site utilise une API de système de réservation externe pour afficher la disponibilité des appartements. Depuis le navigateur, nous n'envoyons que l'identifiant de l'appartement et les dates, sans détails personnels sur les invités."
        ]
      },
      {
        id: "use-of-data",
        heading: "3. Finalités d'utilisation",
        content: [
          "Gérer et répondre à vos demandes et demandes de réservation.",
          "Maintenir le contact avec vous concernant des offres, questions et coordination de séjours.",
          "Améliorer nos services et le contenu du site (sous réserve de toute mise à jour future de la présente politique)."
        ]
      },
      {
        id: "sharing",
        heading: "4. Partage des informations avec des tiers",
        content: [
          "Nous pouvons partager des informations avec des prestataires technologiques qui nous aident à faire fonctionner le site et à gérer les demandes (tels que Make/Integromat, l'hébergeur du site et le système de réservation externe).",
          "Nous pouvons partager des informations avec les propriétaires des appartements et d'autres parties impliquées dans la prestation du service d'hébergement.",
          "Nous divulguerons des informations si la loi, une décision de justice ou une autorité compétente nous l'impose. Nous ne vendons ni ne louons vos informations personnelles à des tiers non liés à des fins de marketing indépendant."
        ]
      },
      {
        id: "security",
        heading: "5. Conservation et sécurité des données",
        content: [
          "Les informations envoyées via le formulaire de contact sont stockées dans des systèmes tiers que nous utilisons pour la gestion des demandes.",
          "Nous prenons des mesures raisonnables pour protéger les informations contre l'accès non autorisé, l'utilisation abusive ou la divulgation, mais aucun système n'est totalement sécurisé.",
          "Vous ne devez pas considérer ce document comme une garantie de sécurité absolue."
        ]
      },
      {
        id: "rights",
        heading: "6. Vos droits",
        content: [
          "Sous réserve du droit applicable, vous pouvez nous contacter pour consulter, corriger ou supprimer les informations personnelles vous concernant, dans la mesure où la loi le permet.",
          "Pour toute demande relative à vos données personnelles, vous pouvez nous écrire à : info@goldhouse-apart.com."
        ]
      },
      {
        id: "retention",
        heading: "7. Durée de conservation",
        content: [
          "Nous conservons les informations personnelles uniquement pendant la durée nécessaire aux finalités pour lesquelles elles ont été fournies et conformément aux exigences légales (par exemple en matière de gestion de la relation client ou d'obligations légales/financières)."
        ]
      },
      {
        id: "transfer",
        heading: "8. Transferts internationaux de données",
        content: [
          "L'utilisation de systèmes tiers peut impliquer un transfert d'informations en dehors d'Israël ou de l'UE, selon le cas. Ces transferts seront effectués conformément au droit applicable et aux politiques de confidentialité de ces prestataires."
        ]
      },
      {
        id: "changes",
        heading: "9. Modifications de la politique",
        content: [
          "Nous pouvons mettre à jour la présente Politique de Confidentialité de temps à autre. La version mise à jour sera publiée sur le site et entrera en vigueur dès sa publication.",
          "La poursuite de l'utilisation du site après de telles modifications vaut acceptation de la politique mise à jour."
        ]
      },
      {
        id: "contact",
        heading: "10. Contact",
        content: [
          "Pour toute question concernant cette Politique de Confidentialité, vous pouvez nous contacter :",
          "E‑mail : info@goldhouse-apart.com",
          "Téléphone : +972-58-420-4297"
        ]
      }
    ]
  },
  es: {
    title: "Política de Privacidad – Gold House Luxury Rentals",
    updatedAt: "Última actualización: noviembre de 2025",
    sections: [
      {
        id: "intro",
        heading: "1. General",
        content: [
          "Gold House Luxury Rentals (\"nosotros\", \"el sitio\") respeta la privacidad de sus usuarios.",
          "Esta política explica qué información recopilamos, cómo la usamos, con quién puede compartirse y cómo se protege.",
          "El uso del sitio implica la aceptación de esta Política de Privacidad. Si no estás de acuerdo, por favor no utilices el sitio."
        ]
      },
      {
        id: "data-we-collect",
        heading: "2. Información que recopilamos",
        content: [
          "Cuando te pones en contacto con nosotros a través del formulario de contacto, nos proporcionas: nombre, dirección de correo electrónico, número de teléfono (opcional) y el contenido de tu mensaje.",
          "Esta información se envía a un servicio externo (Make/Integromat) que nos ayuda a gestionar y responder a las consultas.",
          "También puedes contactarnos directamente por correo electrónico, teléfono o WhatsApp. En estos casos, la información se maneja según las políticas de privacidad de los proveedores correspondientes (correo, compañía telefónica, WhatsApp).",
          "Según el código actual del sitio, no utilizamos cookies para seguimiento de marketing y no ejecutamos Google Analytics ni Meta Pixel en el lado del cliente.",
          "El sitio utiliza una API de un sistema de reservas externo para mostrar la disponibilidad de los apartamentos. Desde el navegador solo enviamos el ID del apartamento y el rango de fechas, sin detalles personales de los huéspedes."
        ]
      },
      {
        id: "use-of-data",
        heading: "3. Finalidades de uso",
        content: [
          "Gestionar y responder a tus consultas y solicitudes de reserva.",
          "Mantener el contacto contigo en relación con ofertas, preguntas y coordinación de estancias.",
          "Mejorar nuestros servicios y el contenido del sitio (sujeto a futuras actualizaciones de esta política)."
        ]
      },
      {
        id: "sharing",
        heading: "4. Compartir información con terceros",
        content: [
          "Podemos compartir información con proveedores tecnológicos que nos ayudan a operar el sitio y gestionar las consultas (como Make/Integromat, el proveedor de hosting y el sistema de reservas externo).",
          "Podemos compartir información con los propietarios de los apartamentos y otras partes involucradas en la prestación del servicio de alojamiento.",
          "Revelaremos información si la ley, una orden judicial o una autoridad competente así lo exige. No vendemos ni alquilamos tu información personal a terceros no relacionados con fines de marketing independiente."
        ]
      },
      {
        id: "security",
        heading: "5. Almacenamiento y seguridad de los datos",
        content: [
          "La información enviada a través del formulario de contacto se almacena en sistemas de terceros que utilizamos para gestionar las consultas.",
          "Tomamos medidas razonables para proteger la información contra el acceso no autorizado, el uso indebido o la divulgación, pero ningún sistema es totalmente seguro.",
          "No debes considerar este documento como una garantía de seguridad absoluta."
        ]
      },
      {
        id: "rights",
        heading: "6. Tus derechos",
        content: [
          "Sujeto a la legislación aplicable, puedes ponerte en contacto con nosotros para revisar, corregir o eliminar la información personal sobre ti, en la medida en que la ley lo permita.",
          "Para cualquier solicitud relacionada con tus datos personales, puedes escribirnos a: info@goldhouse-apart.com."
        ]
      },
      {
        id: "retention",
        heading: "7. Conservación de datos",
        content: [
          "Conservamos la información personal solo durante el tiempo necesario para los fines para los que fue proporcionada y según lo exijan las leyes aplicables (por ejemplo, para la gestión de la relación con el cliente u obligaciones legales/financieras)."
        ]
      },
      {
        id: "transfer",
        heading: "8. Transferencias internacionales de datos",
        content: [
          "El uso de sistemas de terceros puede implicar la transferencia de información fuera de Israel o de la UE, según corresponda. Dichas transferencias se realizarán de acuerdo con la legislación aplicable y las políticas de privacidad de dichos proveedores."
        ]
      },
      {
        id: "changes",
        heading: "9. Cambios en esta política",
        content: [
          "Podemos actualizar esta Política de Privacidad de vez en cuando. La versión actualizada se publicará en el sitio y entrará en vigor en el momento de su publicación.",
          "El uso continuado del sitio tras dichos cambios implica la aceptación de la política actualizada."
        ]
      },
      {
        id: "contact",
        heading: "10. Contacto",
        content: [
          "Para cualquier pregunta sobre esta Política de Privacidad, puedes contactar con nosotros en:",
          "Email: info@goldhouse-apart.com",
          "Teléfono: +972-58-420-4297"
        ]
      }
    ]
  },
  he: {
    title: "מדיניות פרטיות – Gold House Luxury Rentals",
    updatedAt: "עודכן לאחרונה: נובמבר 2025",
    sections: [
      {
        id: "intro",
        heading: "1. כללי",
        content: [
          "אתר Gold House Luxury Rentals (\"האתר\", \"אנחנו\") מכבד את פרטיות המשתמשים.",
          "מדיניות זו מסבירה איזה מידע אנו אוספים, כיצד אנו משתמשים בו, למי הוא נמסר וכיצד נשמרת אבטחתו.",
          "השימוש באתר מהווה הסכמה למדיניות זו. אם אינך מסכים – אנא הימנע משימוש באתר."
        ]
      },
      {
        id: "data-we-collect",
        heading: "2. איזה מידע אנו אוספים",
        content: [
          "כאשר אתה יוצר איתנו קשר דרך טופס יצירת הקשר באתר, אתה מספק לנו מרצון: שם מלא, כתובת אימייל, מספר טלפון (אופציונלי) ותוכן ההודעה.",
          "מידע זה נשלח באופן אוטומטי למערכת חיצונית לניהול פניות (שירות Make/Integromat) לצורך טיפול בפנייה שלך.",
          "בנוסף, ניתן ליצור איתנו קשר ישירות באמצעות אימייל, טלפון או WhatsApp. במקרים אלו, המידע מנוהל בהתאם למדיניות הפרטיות של ספקי השירות הרלוונטיים (ספק הדוא\"ל, חברת הסלולר, WhatsApp).",
          "לפי הקוד הנוכחי של האתר, איננו משתמשים בעוגיות (Cookies) למעקב שיווקי, ואיננו מפעילים Google Analytics או Meta Pixel בצד הלקוח.",
          "האתר עושה שימוש בממשק API של מערכת הזמנות חיצונית לצורך הצגת זמינות דירות. מהדפדפן נשלחים רק מזהה הדירה וטווח תאריכים, ללא פרטים מזהים של האורחים."
        ]
      },
      {
        id: "use-of-data",
        heading: "3. מטרות השימוש במידע",
        content: [
          "מענה לפניות הנשלחות דרך טופס יצירת הקשר, אימייל, טלפון או WhatsApp.",
          "יצירת קשר חוזר איתך לצורך הצעות מחיר, שאלות, מידע נוסף ותיאום הזמנות.",
          "שיפור השירות והתוכן באתר, בכפוף לכל עדכון עתידי של מדיניות זו."
        ]
      },
      {
        id: "sharing",
        heading: "4. מסירת מידע לצדדים שלישיים",
        content: [
          "אנו עשויים להעביר מידע לספקי שירות טכנולוגיים המסייעים לנו בהפעלת האתר וניהול הפניות (כגון Make/Integromat, חברת האחסון ומערכת ההזמנות החיצונית).",
          "ייתכן שנעביר מידע לבעלי הדירות ולגורמים נוספים המעורבים במתן שירות האירוח.",
          "נמסור מידע אם נהיה מחויבים לכך על פי דין, צו שיפוטי או דרישה מוסמכת של רשות. איננו מוכרים או משכירים מידע אישי לגורמים מסחריים שאינם קשורים למתן השירות."
        ]
      },
      {
        id: "security",
        heading: "5. שמירת מידע ואבטחה",
        content: [
          "המידע שנשלח דרך טופס יצירת הקשר נשמר במערכות צד שלישי המשמשות אותנו לניהול פניות.",
          "אנו נוקטים באמצעים סבירים להגנה על המידע מפני גישה לא מורשית, שימוש לרעה או חשיפה, אך אין באפשרותנו להבטיח אבטחה מוחלטת.",
          "אין לראות במדיניות זו התחייבות לביטחון מוחלט של המידע."
        ]
      },
      {
        id: "rights",
        heading: "6. זכויותיך בנוגע למידע",
        content: [
          "בהתאם לדין החל (למשל חוק הגנת הפרטיות בישראל, ככל שחל), עומדת לך הזכות לעיין במידע עליך, לבקש את תיקונו או מחיקתו – הכל בכפוף להגבלות ולדרישות הדין.",
          "למימוש זכויותיך, ניתן לפנות אלינו בדוא\"ל: info@goldhouse-apart.com."
        ]
      },
      {
        id: "retention",
        heading: "7. תקופת שמירת המידע",
        content: [
          "אנו נשמור את המידע האישי שלך רק כל עוד הוא נחוץ למטרות שלשמן נמסר, וכן ככל שנדרש על פי כל דין (למשל לצורך ניהול קשרי לקוחות או עמידה בחובות חוקיות/חשבונאיות)."
        ]
      },
      {
        id: "transfer",
        heading: "8. העברת מידע מחוץ לישראל / לאיחוד האירופי",
        content: [
          "ככל שהשימוש במערכות צד שלישי כרוך בהעברת מידע אל מחוץ לישראל (או אל מחוץ לאיחוד האירופי, ככל שרלוונטי), העברה זו תיעשה בהתאם לדין החל ובכפוף למדיניות הפרטיות של אותם ספקי שירות."
        ]
      },
      {
        id: "changes",
        heading: "9. עדכון מדיניות הפרטיות",
        content: [
          "אנו רשאים לעדכן מדיניות פרטיות זו מעת לעת. הגרסה המעודכנת תפורסם באתר ותיכנס לתוקף במועד פרסומה.",
          "שימוש מתמשך באתר לאחר פרסום גרסה מעודכנת מהווה הסכמה למדיניות המעודכנת."
        ]
      },
      {
        id: "contact",
        heading: "10. יצירת קשר",
        content: [
          "לשאלות בנוגע למדיניות פרטיות זו, ניתן לפנות אלינו בכתובת:",
          "דוא\"ל: info@goldhouse-apart.com",
          "טלפון: 058-420-4297"
        ]
      }
    ]
  },
}

export const PrivacyPolicyProvider = ({ children }) => {
  const { language } = useLanguage()
  const policy = privacyPolicies[language] || privacyPolicies.en

  return (
    <PrivacyPolicyContext.Provider value={{ policy }}>
      {children}
    </PrivacyPolicyContext.Provider>
  )
}
